import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Eagerly load the landing page for fast LCP
import Landing from "./pages/Landing";

// Lazy load all other routes to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Demo = lazy(() => import("./pages/Demo"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Settings = lazy(() => import("./pages/Settings"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MortgagePage = lazy(() => import("./pages/loans/MortgagePage"));
const PersonalLoanPage = lazy(() => import("./pages/loans/PersonalLoanPage"));
const AutoLoanPage = lazy(() => import("./pages/loans/AutoLoanPage"));
const StudentLoanPage = lazy(() => import("./pages/loans/StudentLoanPage"));
const BusinessLoanPage = lazy(() => import("./pages/loans/BusinessLoanPage"));

// Minimal loading fallback that doesn't affect LCP
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
              <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
              <Route path="/onboarding" element={<Suspense fallback={<PageLoader />}><Onboarding /></Suspense>} />
              <Route path="/demo" element={<Suspense fallback={<PageLoader />}><Demo /></Suspense>} />
              <Route path="/upgrade" element={<Suspense fallback={<PageLoader />}><Upgrade /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={<PageLoader />}><Terms /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
              <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
              <Route path="/loans/mortgage" element={<Suspense fallback={<PageLoader />}><MortgagePage /></Suspense>} />
              <Route path="/loans/personal" element={<Suspense fallback={<PageLoader />}><PersonalLoanPage /></Suspense>} />
              <Route path="/loans/auto" element={<Suspense fallback={<PageLoader />}><AutoLoanPage /></Suspense>} />
              <Route path="/loans/student" element={<Suspense fallback={<PageLoader />}><StudentLoanPage /></Suspense>} />
              <Route path="/loans/business" element={<Suspense fallback={<PageLoader />}><BusinessLoanPage /></Suspense>} />
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
